import { useState, useEffect, useCallback } from 'react';
import { usePolling } from '@/shared/hooks/usePolling';
import { getUnreadAlertCount } from '../services/alertsService';
import type { AlertCount } from '../types';

interface UseAlertCountResult extends AlertCount {
  isLoading: boolean;
  hasError: boolean;
  refetch: () => Promise<void>;
}

export function useAlertCount(): UseAlertCountResult {
  const [count, setCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const fetchCount = useCallback(async () => {
    try {
      const response = await getUnreadAlertCount();
      setCount(response.count);
      setHasError(false);
      setIsLoading(false);
    } catch (error) {
      console.error('[useAlertCount] Error fetching unread alert count:', error);
      setHasError(true);
      throw error; // Re-throw to trigger polling backoff
    }
  }, []);

  const handleMaxRetriesExceeded = useCallback(() => {
    setHasError(true);
  }, []);

  const { start, stop } = usePolling({
    callback: fetchCount,
    interval: 15000,
    pauseOnHidden: true,
    enableBackoff: true,
    maxRetries: 3,
    onMaxRetriesExceeded: handleMaxRetriesExceeded,
  });

  useEffect(() => {
    start();
    return () => stop();
  }, [start, stop]);

  const refetch = useCallback(async () => {
    setIsLoading(true);
    try {
      await fetchCount();
    } catch {
      // Error already logged in fetchCount
    }
  }, [fetchCount]);

  return { count, isLoading, hasError, refetch };
}
